// src/components/VideoEngine/Common/KaraokeCaptions.js

import React from "react";
import { useCurrentFrame } from "remotion";

// --- SUB-COMPONENT: Node 3 "General" Static Renderer (Pop-On Style) ---
const GeneralKaraokeWord = ({ word }) => {
  // Path B (Revised): The parent component already handles the "beat" timing.
  // If this word is being rendered, its entire chunk is currently active.
  // We simply render it as solid pink immediately.
  
  // return (
  //   <span
  //     style={{
  //       display: "inline-block",
  //       marginRight: "10px",
  //       marginLeft: "10px",
  //       marginBottom: "4px",
  //       verticalAlign: "middle",
  //       // color: "#bd0ae1", // Always Pink
  //        color: "#ffd000", // Always Yellow
  //       // color: "#f3f3d6", // Always Yellow
  //       fontWeight: 800,
  //       WebkitFontSmoothing: "antialiased",
  //       textRendering: "geometricPrecision",
  //       backfaceVisibility: "hidden",
  //       transform: "translateZ(0)",
  //     }}
  //   >
  //     {word.text}
  //   </span>
  // );
// };

return (
    <span
      style={{
        display: "inline-block",
        marginRight: "10px",
        marginLeft: "10px",
        marginBottom: "4px",
        verticalAlign: "middle",
        
        // 💉 INJECTION: Intercept fill color and underline for search terms
        color: word.isSearchTerm ? "#a08cf7" : "#ffd000",
       // fontStyle: word.isSearchTerm ? "italic" : "normal", // 💉 Swapped underline for italic  //textDecoration: word.isSearchTerm ? "underline" : "none",
        textUnderlineOffset: 8,
        textDecorationThickness: 4,

        // fontWeight: 800,
        fontWeight: word.isSearchTerm ? 500 : 800,
        WebkitFontSmoothing: "antialiased",
        textRendering: "geometricPrecision",
        backfaceVisibility: "hidden",
        transform: "translateZ(0)",
      }}
    >
      {word.text}
    </span>
  );
};

// --- SUB-COMPONENT: Standard Highlight Renderer ---
const KaraokeWord = ({ word, frame, theme, highlightAll }) => {
  const isActive = frame >= word.startFrame && frame < word.endFrame;
  const ACTIVE_COLOR = "#bd0ae1"; // BOLD PINK (Standard Highlight)

  // ⛔ THE BLOCKLIST (Stop Words)
  const STOP_WORDS = ["a", "an", "the", "and", "but", "or", "for", "nor", "on", "at", "to", "from", "by", "is", "as", "of", "in", "it"];
  const cleanText = word.text.toLowerCase().replace(/[^a-z0-9]/g, '');
  const isStopWord = STOP_WORDS.includes(cleanText);

  // 🎛️ TUNING KNOBS
  const OPACITY_ACTIVE_EMPHASIS = 1.0;
  const OPACITY_ACTIVE_FILLER = 0.9;
  const OPACITY_INACTIVE = 0.7; // Slightly higher visibility for inactive text

  // LOGIC 1: Emphasis (Is it important?)
  const isEmphasized = highlightAll || (!isStopWord && (word.emphasis !== false));

  // LOGIC 4: Opacity
  const targetOpacity = isActive
    ? (isEmphasized ? OPACITY_ACTIVE_EMPHASIS : OPACITY_ACTIVE_FILLER)
    : OPACITY_INACTIVE;

  return (
    <span
      style={{
        display: "inline-grid",
        gridTemplateColumns: "1fr",
        alignItems: "center",
        marginRight: "10px",
        marginLeft: "10px",
        marginBottom: "4px",
        verticalAlign: "middle",
      }}
    >
      {/* 👻 THE GHOST: Max-Weight (Reserves Layout Space) */}
      <span style={{
        gridArea: "1 / 1 / 2 / 2",
        fontWeight: 900, 
        opacity: 0,
        visibility: "hidden",
        pointerEvents: "none",
      }}>
        {word.text}
      </span>

      {/* 🎭 THE ACTOR: Locked Weight, GPU Transformed */}
      <span style={{
        gridArea: "1 / 1 / 2 / 2",
        display: "inline-block",
        
        // --- 🚀 THE IRONCLAD FILL STRATEGY ---
        opacity: (isActive && isEmphasized) ? undefined : targetOpacity,
        
        // 💉 INJECTION: Intercept fill colors if this is our target search phrase
        WebkitTextFillColor: word.isSearchTerm ? (theme?.secondary || "cyan") : (isActive && isEmphasized ? ACTIVE_COLOR : "white"),
        color: word.isSearchTerm ? (theme?.secondary || "cyan") : (isActive && isEmphasized ? "transparent" : "white"),
        textDecoration: word.isSearchTerm ? "underline" : "none",
        textUnderlineOffset: 8,
        textDecorationThickness: 4,

        isolation: "isolate",
        fontWeight: 800, 
        wordBreak: "break-word",
        WebkitFontSmoothing: "antialiased",
        textRendering: "geometricPrecision",
        backfaceVisibility: "hidden",
        transform: "translateZ(0)",
      }}
      >
        {word.text}
      </span>
    </span>
  );
};

// --- MAIN COMPONENT: Sentence Wrapper ---
export const KaraokeCaptions = ({
  captions = [],
  theme,
  delay = 0,
  highlightAll,
  captionStyle, // <--- Injecting the localized setting 
  align = "flex-start" // 💉 NEW INJECTION: Flexbox alignment prop
}) => {
  const frame = useCurrentFrame();
  const WORDS_PER_SCREEN = 7;

  // Evaluate the style hierarchy (Direct Prop > Theme > Default)
  const effectiveStyle = captionStyle || theme?.captionStyle || 'social';
  const effectiveHighlightAll = highlightAll ?? theme?.highlightAll ?? true;

  const currentAudioFrame = frame - delay;

  // --- THE BATON PASS PRE-PROCESSOR ---
  const processedCaptions = React.useMemo(() => {
    if (!captions || captions.length === 0) return [];

    return captions.map((word, index) => {
      const nextWord = captions[index + 1];
      const newEndFrame = (nextWord && (nextWord.startFrame - word.endFrame) < 15)
        ? nextWord.startFrame
        : word.endFrame;

      return { ...word, endFrame: newEndFrame };
    });
  }, [captions]);

  // 1. CHUNK LOGIC (Pagination)
  const chunks = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < processedCaptions.length; i += WORDS_PER_SCREEN) {
      result.push(processedCaptions.slice(i, i + WORDS_PER_SCREEN));
    }
    return result;
  }, [processedCaptions]);

  // 2. FIND ACTIVE CHUNK (High-Water Mark Strategy)
  let targetIndex = 0;
  for (let i = chunks.length - 1; i >= 0; i--) {
    const chunkStartFrame = chunks[i]?.[0]?.startFrame ?? 0;
    if (currentAudioFrame >= chunkStartFrame) {
      targetIndex = i;
      break;
    }
  }

  const visibleWords = chunks[targetIndex] || [];

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: align, 
        textAlign: align === "center" ? "center" : "left", // 💉 NEW: Text-level alignment fallback
        width: "100%",
        fontFamily: theme?.fontMain || "'Outfit', sans-serif",
        textTransform: "none",
        lineHeight: 1.4,
      }}
    >
      {visibleWords.map((word, i) => (
        effectiveStyle === 'general' ? (
          // Node 3: Render via the static bypass block
          <GeneralKaraokeWord
            key={`chunk-${targetIndex}-word-${i}`}
            word={word}
            frame={frame - delay}
          />
        ) : (
          // Legacy: Render via the complex highlighter
          <KaraokeWord
            key={`chunk-${targetIndex}-word-${i}`}
            word={word}
            frame={frame - delay}
            theme={theme}
            highlightAll={effectiveHighlightAll}
          />
        )
      ))}
    </div>
  );
};